// Tool Registry - Centralized metadata and self-test functions for all GenieBot tools

export interface ToolSelfTestResult {
  passed: boolean;
  message: string;
  details?: string;
}

export interface ToolMetadata {
  id: string;
  name: string;
  description: string;
  route: string;
  category: 'Security' | 'Finance' | 'Productivity' | 'Lifestyle' | 'Education' | 'Developer' | 'Creative' | 'Career' | 'AI';
  requiresAuth: boolean;
  requiresSubscription: boolean;
  integrations: string[];
  selfTest: () => Promise<ToolSelfTestResult>;
}

// Self-test helper functions
const createBasicTest = (toolName: string): (() => Promise<ToolSelfTestResult>) => {
  return async () => {
    try {
      // Basic test: verify the tool component exists and can be referenced
      return {
        passed: true,
        message: `${toolName} component is available`,
      };
    } catch (error) {
      return {
        passed: false,
        message: `${toolName} failed to load`,
        details: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  };
};

// Tool Registry
export const TOOL_REGISTRY: ToolMetadata[] = [
  // Free Tools
  {
    id: 'password-generator',
    name: 'Password Generator',
    description: 'Generate strong, secure passwords with customizable options',
    route: '/tools',
    category: 'Security',
    requiresAuth: false,
    requiresSubscription: false,
    integrations: [],
    selfTest: createBasicTest('Password Generator'),
  },
  {
    id: 'currency-converter',
    name: 'Currency Converter',
    description: 'Convert between currencies using live ECB reference rates',
    route: '/tools',
    category: 'Finance',
    requiresAuth: false,
    requiresSubscription: false,
    integrations: ['ECB API'],
    selfTest: createBasicTest('Currency Converter'),
  },
  {
    id: 'crypto-converter',
    name: 'Cryptocurrency Converter',
    description: 'Convert between cryptocurrencies and fiat with live prices',
    route: '/tools',
    category: 'Finance',
    requiresAuth: false,
    requiresSubscription: false,
    integrations: ['CoinGecko API'],
    selfTest: createBasicTest('Crypto Converter'),
  },
  {
    id: 'current-weather',
    name: 'Current Weather',
    description: 'Check live weather conditions for any location worldwide',
    route: '/tools',
    category: 'Productivity',
    requiresAuth: false,
    requiresSubscription: false,
    integrations: ['OpenWeatherMap API'],
    selfTest: createBasicTest('Current Weather'),
  },
  {
    id: 'calculator',
    name: 'Calculator',
    description: 'Advanced calculator with basic and scientific modes',
    route: '/tools',
    category: 'Productivity',
    requiresAuth: false,
    requiresSubscription: false,
    integrations: [],
    selfTest: createBasicTest('Calculator'),
  },
  {
    id: 'travel-agent',
    name: 'TravelGPT',
    description: 'Plan a date-aware, budget-aware itinerary with day-by-day details',
    route: '/tools',
    category: 'Lifestyle',
    requiresAuth: true,
    requiresSubscription: false,
    integrations: ['Lovable AI'],
    selfTest: createBasicTest('TravelGPT'),
  },
  
  // Previously Premium Tools (now free)
  {
    id: 'relationship-coach',
    name: 'Relationship Coach',
    description: 'Get empathetic guidance for relationship challenges',
    route: '/tools',
    category: 'Lifestyle',
    requiresAuth: true,
    requiresSubscription: false,
    integrations: ['Lovable AI'],
    selfTest: createBasicTest('Relationship Coach'),
  },
  {
    id: 'personal-chef',
    name: 'Personal Chef Assistant',
    description: 'Transform ingredients into recipes',
    route: '/tools',
    category: 'Lifestyle',
    requiresAuth: true,
    requiresSubscription: false,
    integrations: ['Lovable AI'],
    selfTest: createBasicTest('Personal Chef'),
  },
  {
    id: 'math-professor',
    name: 'Math Professor',
    description: 'Patient, visual math tutor',
    route: '/tools',
    category: 'Education',
    requiresAuth: true,
    requiresSubscription: false,
    integrations: ['Lovable AI'],
    selfTest: createBasicTest('Math Professor'),
  },
  {
    id: 'geniebot-workflow',
    name: 'GenieBot Workflow',
    description: 'Generate n8n automation workflows',
    route: '/tools',
    category: 'Developer',
    requiresAuth: true,
    requiresSubscription: false,
    integrations: ['Lovable AI'],
    selfTest: createBasicTest('GenieBot Workflow'),
  },
  {
    id: 'cyber-security',
    name: 'Cyber Security Assistant',
    description: 'Analyze suspicious command lines',
    route: '/tools',
    category: 'Security',
    requiresAuth: true,
    requiresSubscription: false,
    integrations: ['Lovable AI'],
    selfTest: createBasicTest('Cyber Security'),
  },
  {
    id: 'code-assistant',
    name: 'Code Assistant',
    description: 'Security-focused coding copilot',
    route: '/tools',
    category: 'Developer',
    requiresAuth: true,
    requiresSubscription: false,
    integrations: ['Lovable AI'],
    selfTest: createBasicTest('Code Assistant'),
  },
  {
    id: 'blog-assistant',
    name: 'Blog Assistant',
    description: 'AI-powered blog post generation',
    route: '/tools',
    category: 'Creative',
    requiresAuth: true,
    requiresSubscription: false,
    integrations: ['Lovable AI'],
    selfTest: createBasicTest('Blog Assistant'),
  },
  {
    id: 'geniebot-rezzy',
    name: 'AI Resume Enhancer',
    description: 'Upgrade your resume with AI',
    route: '/tools',
    category: 'Career',
    requiresAuth: true,
    requiresSubscription: false,
    integrations: ['Lovable AI'],
    selfTest: createBasicTest('Rezzy'),
  },
  {
    id: 'language-translator',
    name: 'Language Translator',
    description: 'AI-powered translation for 30+ languages',
    route: '/tools',
    category: 'Productivity',
    requiresAuth: true,
    requiresSubscription: false,
    integrations: ['Lovable AI'],
    selfTest: createBasicTest('Language Translator'),
  },
  {
    id: 'grammar-assistant',
    name: 'Grammar Assistant',
    description: 'AI grammar checking and writing improvement',
    route: '/tools',
    category: 'Productivity',
    requiresAuth: true,
    requiresSubscription: false,
    integrations: ['Lovable AI'],
    selfTest: createBasicTest('Grammar Assistant'),
  },
  {
    id: 'seo-analyzer',
    name: 'SEO Content Analyzer',
    description: 'AI-powered SEO analysis',
    route: '/tools',
    category: 'Creative',
    requiresAuth: true,
    requiresSubscription: false,
    integrations: ['Lovable AI'],
    selfTest: createBasicTest('SEO Analyzer'),
  },
  {
    id: 'color-palette',
    name: 'Color Palette Generator',
    description: 'AI-powered color schemes with WCAG checker',
    route: '/tools',
    category: 'Creative',
    requiresAuth: true,
    requiresSubscription: false,
    integrations: ['Lovable AI'],
    selfTest: createBasicTest('Color Palette'),
  },
  {
    id: 'ai-video-generator',
    name: 'AI Video Generator',
    description: 'Generate videos from text',
    route: '/tools',
    category: 'AI',
    requiresAuth: true,
    requiresSubscription: false,
    integrations: ['Replicate API'],
    selfTest: createBasicTest('AI Video Generator'),
  },
  
  // Special Routes
  {
    id: 'chat',
    name: 'GenieBot Chat',
    description: 'AI chat assistant',
    route: '/chat',
    category: 'AI',
    requiresAuth: true,
    requiresSubscription: false,
    integrations: ['Lovable AI', 'Supabase'],
    selfTest: createBasicTest('Chat'),
  },
  {
    id: 'geniebot-particles',
    name: 'GenieBot Particles',
    description: 'Interactive particle avatar with face tracking',
    route: '/geniebot-particles',
    category: 'AI',
    requiresAuth: false,
    requiresSubscription: false,
    integrations: ['MediaPipe'],
    selfTest: createBasicTest('GenieBot Particles'),
  },
  {
    id: 'ui-spec-generator',
    name: 'GenieBot WebDesign',
    description: 'AI-powered UI specification generator',
    route: '/tools/ui-spec',
    category: 'Developer',
    requiresAuth: true,
    requiresSubscription: false,
    integrations: ['Lovable AI', 'Supabase'],
    selfTest: createBasicTest('UI Spec Generator'),
  },
  {
    id: 'ai-image-generator',
    name: 'AI Image Generator',
    description: 'Transform text into stunning images',
    route: '/ai-image-generator',
    category: 'AI',
    requiresAuth: false,
    requiresSubscription: false,
    integrations: ['Lovable AI'],
    selfTest: createBasicTest('AI Image Generator'),
  },
  {
    id: 'ai-humanizer',
    name: 'AI Humanizer',
    description: 'Transform AI text into natural writing',
    route: '/ai-humanizer',
    category: 'AI',
    requiresAuth: false,
    requiresSubscription: false,
    integrations: ['Lovable AI'],
    selfTest: createBasicTest('AI Humanizer'),
  },
  {
    id: 'geniebot-everyday-suite',
    name: 'Everyday Suite',
    description: '10 AI-powered productivity tools',
    route: '/geniebot-everyday-suite',
    category: 'Productivity',
    requiresAuth: true,
    requiresSubscription: false,
    integrations: ['Lovable AI'],
    selfTest: createBasicTest('Everyday Suite'),
  },
  {
    id: 'contract-risk-analyzer',
    name: 'Contract Risk Analyzer',
    description: 'AI-powered contract analysis with risk scoring and clause detection',
    route: '/contract-analyzer',
    category: 'Security',
    requiresAuth: true,
    requiresSubscription: false,
    integrations: ['Lovable AI', 'Supabase'],
    selfTest: createBasicTest('Contract Risk Analyzer'),
  },
  {
    id: 'pdf-storybook-architect',
    name: 'PDF StoryBook Architect',
    description: 'Generate complete, original short-fiction manuscripts with page breaks, ready for PDF export',
    route: '/pdf-storybook-architect',
    category: 'Creative',
    requiresAuth: true,
    requiresSubscription: false,
    integrations: ['Lovable AI'],
    selfTest: createBasicTest('PDF StoryBook Architect'),
  },
  {
    id: 'geniebot-tutor',
    name: 'GenieBot Tutor',
    description: 'Create polished, professional how-to guides with AI',
    route: '/geniebot-tutor',
    category: 'Education',
    requiresAuth: true,
    requiresSubscription: false,
    integrations: ['Lovable AI'],
    selfTest: createBasicTest('GenieBot Tutor'),
  },
];

// Get tool by ID
export const getToolById = (id: string): ToolMetadata | undefined => {
  return TOOL_REGISTRY.find(tool => tool.id === id);
};

// Get tools by category
export const getToolsByCategory = (category: ToolMetadata['category']): ToolMetadata[] => {
  return TOOL_REGISTRY.filter(tool => tool.category === category);
};

// Get free tools only
export const getFreeTools = (): ToolMetadata[] => {
  return TOOL_REGISTRY.filter(tool => !tool.requiresSubscription);
};

// Get premium tools only
export const getPremiumTools = (): ToolMetadata[] => {
  return TOOL_REGISTRY.filter(tool => tool.requiresSubscription);
};

// Run all self-tests
export const runAllSelfTests = async (): Promise<{ toolId: string; result: ToolSelfTestResult }[]> => {
  const results = await Promise.all(
    TOOL_REGISTRY.map(async (tool) => ({
      toolId: tool.id,
      result: await tool.selfTest(),
    }))
  );
  return results;
};

// Get unique categories
export const getCategories = (): ToolMetadata['category'][] => {
  return [...new Set(TOOL_REGISTRY.map(tool => tool.category))];
};

// Get unique integrations
export const getIntegrations = (): string[] => {
  const integrations = new Set<string>();
  TOOL_REGISTRY.forEach(tool => {
    tool.integrations.forEach(i => integrations.add(i));
  });
  return [...integrations];
};
